import { Box, Theme, SxProps, Typography } from "@mui/material";
import {
  addDays,
  addMinutes,
  differenceInCalendarDays,
  differenceInMinutes,
} from "date-fns";
import { useRef } from "react";
import { Provider } from "@/components/common/providerSection";
import { TEXT_PRIMARY_DARK } from "@/config/mui/colorPalette";
import useMedspaTimezone from "@/hooks/common/useMedspaTimezone";
import { ClientForAppointmentCreation } from "../../common/clientSection";
import { AvailableService } from "./appointmentServicesSection";
import { AppointmentServiceInput } from "./multiProviderAppointmentDrawerContext";
import { ServiceResourcesInfo } from "./serviceResourcesInfo";
import { TimeSelectionCalendar } from "./timeSelectionCalendar";
import { TimeSelectionForm } from "./timeSelectionForm";

type Props = {
  appointmentId?: string;
  date: Date;
  appointmentServices: AppointmentServiceInput[];
  onAppointmentServicesChange: (
    appointmentsServices: AppointmentServiceInput[]
  ) => void;
  providers: Provider[];
  availableServices: AvailableService[];
  client: ClientForAppointmentCreation;
  isRebooking: boolean;
  sx?: SxProps<Theme>;
};

export const TimeSelectionContent = ({
  appointmentId,
  date,
  appointmentServices,
  providers,
  availableServices,
  client,
  isRebooking,
  onAppointmentServicesChange,
  sx,
}: Props) => {
  const { medspaReadableTimezone } = useMedspaTimezone();
  const calendarContainerRef = useRef<HTMLDivElement>(null);

  /**
   * Updates the date that calendar is focused on, and updates the time of the
   * appointment services to match the new date.
   * @TODO: Good candidate for unit tests
   */
  const handleUpdateDate = (newDate: Date) => {
    const daysDifference = differenceInCalendarDays(newDate, date);
    onAppointmentServicesChange(
      appointmentServices.map((appointmentService) => ({
        ...appointmentService,
        startTime: addDays(
          new Date(appointmentService.startTime),
          daysDifference
        ),
        endTime: addDays(new Date(appointmentService.endTime), daysDifference),
      }))
    );
  };

  /**
   * Validates if the event overlaps with the previous service
   * If it does, it adjusts the start and end times to avoid overlap
   * @TODO: Good candidate for unit tests
   */
  const validateEventOverlap = (index: number, start: Date, end: Date) => {
    if (index > 0) {
      const previousService = appointmentServices[index - 1];

      if (start < previousService.endTime) {
        // if we updated appointment service startTime before the previous service endTime,
        // we need to adjust the time, as they cannot overlap
        const duration = differenceInMinutes(end, start);
        start = new Date(previousService.endTime);
        end = addMinutes(new Date(start), duration);
      }
    }

    return { safeStart: start, safeEnd: end };
  };

  /**
   * Moves the time of the appointment service at the given index, and updates
   * the time of the following appointment services.
   * @TODO: Good candidate for unit tests
   */
  const handleUpdateAppointmentTime = (options: {
    newStartTime: Date;
    newEndTime: Date;
    index?: number;
  }) => {
    const { newStartTime, newEndTime, index } = options;
    const shouldRescheduleWholeAppointment = index === undefined;

    if (shouldRescheduleWholeAppointment) {
      const prevStartTime = appointmentServices[0].startTime;
      const difference = differenceInMinutes(newStartTime, prevStartTime, {
        roundingMethod: "ceil",
      });

      onAppointmentServicesChange(
        appointmentServices.map((service) => ({
          ...service,
          startTime: addMinutes(service.startTime, difference),
          endTime: addMinutes(service.endTime, difference),
        }))
      );

      return;
    }

    const { safeStart, safeEnd } = validateEventOverlap(
      index,
      newStartTime,
      newEndTime
    );
    const { endTime: prevEndTime } = appointmentServices[index];
    const endTimeShift = differenceInMinutes(safeEnd, prevEndTime);
    const newAppointmentServices = [...appointmentServices];
    newAppointmentServices[index] = {
      ...appointmentServices[index],
      startTime: safeStart,
      endTime: safeEnd,
    };
    if (endTimeShift !== 0) {
      for (let i = index + 1; i < newAppointmentServices.length; i++) {
        newAppointmentServices[i].startTime = addMinutes(
          new Date(newAppointmentServices[i].startTime),
          endTimeShift
        );
        newAppointmentServices[i].endTime = addMinutes(
          new Date(newAppointmentServices[i].endTime),
          endTimeShift
        );
      }
    }
    onAppointmentServicesChange(newAppointmentServices);
  };

  return (
    <Box
      sx={{
        width: "360px",
        ...sx,
      }}
    >
      <ServiceResourcesInfo appointmentServices={appointmentServices} />
      <Box sx={{ overflow: "auto" }} ref={calendarContainerRef}>
        <TimeSelectionForm
          date={date}
          appointmentServices={appointmentServices}
          isRebooking={isRebooking}
          onDateChange={handleUpdateDate}
          onAppointmentTimeChange={handleUpdateAppointmentTime}
        />
        <Typography
          variant="paragraphSmall"
          color="text.secondary"
          sx={{
            my: 1,
          }}
        >
          Please note: You operate in{" "}
          <strong style={{ color: TEXT_PRIMARY_DARK }}>
            {medspaReadableTimezone}
          </strong>{" "}
          time.
        </Typography>
        <TimeSelectionCalendar
          appointmentId={appointmentId}
          date={date}
          appointmentServices={appointmentServices}
          onDateChange={handleUpdateDate}
          onAppointmentTimeChange={handleUpdateAppointmentTime}
          providers={providers}
          availableServices={availableServices}
          client={client}
          calendarContainerRef={calendarContainerRef}
        />
      </Box>
    </Box>
  );
};
